import { CarComparisonData } from '../types/carComparison';
import { FUEL_EMISSION_FACTORS, CO2_DEFAULTS, getEmissionFactor, getDefaultSCC } from '../data/emissionFactors';

export interface CO2Results {
  lifetimeEmissions: number; // Total kg CO₂
  manufacturingEmissions: number; // kg CO₂
  operationalEmissions: number; // kg CO₂
  dollarValue: number; // $ based on Social Cost of Carbon
  tonnesCO2: number; // tonnes CO₂
}

/**
 * Calculate lifetime CO₂ emissions for a vehicle
 */
export function calculateCO2Emissions(data: CarComparisonData): CO2Results | null {
  if (!data.includeCO2Comparison) {
    return null;
  }

  const {
    fuelType,
    region,
    province = '',
    annualMileage,
    ownershipTimeframe,
    fuelEfficiency,
    kWhPer100km = 18, // Default EV efficiency
    manufacturingEmissions = CO2_DEFAULTS.manufacturingEmissionsNew,
    socialCostOfCarbon = getDefaultSCC(region),
    pluginHybridUsage = 'medium'
  } = data;

  // Calculate total km driven
  const totalKm = annualMileage * ownershipTimeframe;

  let operationalEmissions = 0; // kg CO₂

  switch (fuelType) {
    case 'gasoline':
    case 'diesel': {
      // Fuel consumption in liters
      const litersPer100km = fuelEfficiency;
      const totalLiters = (totalKm / 100) * litersPer100km;

      // CO₂ emissions
      const emissionFactor = FUEL_EMISSION_FACTORS[fuelType];
      operationalEmissions = totalLiters * emissionFactor; // kg CO₂
      break;
    }

    case 'electric': {
      // Electricity consumption in kWh
      const totalKWh = (totalKm / 100) * kWhPer100km;

      // CO₂ emissions based on provincial grid
      const gridEmissionFactor = getEmissionFactor(region, province); // g CO₂ per kWh
      operationalEmissions = (totalKWh * gridEmissionFactor) / 1000; // Convert g to kg
      break;
    }

    case 'plugin-hybrid': {
      // Get electric usage percentage
      const electricPercent = CO2_DEFAULTS.pluginHybridElectricPercent[pluginHybridUsage];
      const gasPercent = 1 - electricPercent;

      // Electric portion
      const electricKm = totalKm * electricPercent;
      const electricKWh = (electricKm / 100) * kWhPer100km;
      const gridEmissionFactor = getEmissionFactor(region, province);
      const electricEmissions = (electricKWh * gridEmissionFactor) / 1000;

      // Gas portion
      const gasKm = totalKm * gasPercent;
      const gasLiters = (gasKm / 100) * fuelEfficiency;
      const gasEmissions = gasLiters * FUEL_EMISSION_FACTORS.gasoline;

      operationalEmissions = electricEmissions + gasEmissions;
      break;
    }
  }

  // Total lifecycle emissions
  const lifetimeEmissions = manufacturingEmissions + operationalEmissions;
  const tonnesCO2 = lifetimeEmissions / 1000; // Convert kg to tonnes

  // Dollar value using Social Cost of Carbon
  const dollarValue = tonnesCO2 * socialCostOfCarbon;

  return {
    lifetimeEmissions,
    manufacturingEmissions,
    operationalEmissions,
    dollarValue,
    tonnesCO2
  };
}

/**
 * Calculate CO₂ savings when comparing two vehicles
 */
export function calculateCO2Savings(carA: CO2Results | null, carB: CO2Results | null): {
  tonnesSaved: number;
  dollarValueSaved: number;
  percentReduction: number;
} | null {
  if (!carA || !carB) {
    return null;
  }

  const tonnesSaved = carA.tonnesCO2 - carB.tonnesCO2;
  const dollarValueSaved = carA.dollarValue - carB.dollarValue;
  const percentReduction = carA.tonnesCO2 > 0
    ? (tonnesSaved / carA.tonnesCO2) * 100
    : 0;

  return {
    tonnesSaved,
    dollarValueSaved,
    percentReduction
  };
}

/**
 * Get display string for fuel type
 */
export function getFuelTypeLabel(fuelType: string): string {
  const labels: Record<string, string> = {
    'gasoline': 'Gasoline',
    'diesel': 'Diesel',
    'electric': 'Electric (EV)',
    'plugin-hybrid': 'Plug-in Hybrid'
  };
  return labels[fuelType] || fuelType;
}
